import { useMemo } from 'react';

import type { InboxFeatureBannerConfig } from '@/constants/inboxFeatureBanners';
import { useFeatureFlagValue } from '@/hooks/useFeatureFlag';

type InboxFeatureBannerContent = {
  badgeLabel: string;
  title: string;
  description: string;
  dismissAccessibilityLabel: string;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export function useInboxFeatureBannerContent(
  config: InboxFeatureBannerConfig
): InboxFeatureBannerContent | null {
  const flagValue = useFeatureFlagValue(config.featureFlag);

  return useMemo(() => {
    if (typeof flagValue !== 'object' || flagValue === null || Array.isArray(flagValue)) {
      return null;
    }

    const parsed = flagValue as Record<string, unknown>;

    if (
      !isNonEmptyString(parsed.badgeLabel) ||
      !isNonEmptyString(parsed.title) ||
      !isNonEmptyString(parsed.description) ||
      !isNonEmptyString(parsed.dismissAccessibilityLabel)
    ) {
      return null;
    }

    return {
      badgeLabel: parsed.badgeLabel.trim(),
      title: parsed.title.trim(),
      description: parsed.description.trim(),
      dismissAccessibilityLabel: parsed.dismissAccessibilityLabel.trim(),
    };
  }, [flagValue]);
}

export function useInboxFeatureBannerAnnouncementId(config: InboxFeatureBannerConfig) {
  const flagValue = useFeatureFlagValue(config.featureFlag);

  return useMemo(() => {
    if (typeof flagValue !== 'object' || flagValue === null || Array.isArray(flagValue)) {
      return undefined;
    }

    const parsed = flagValue as Record<string, unknown>;

    return isNonEmptyString(parsed.announcementId) ? parsed.announcementId.trim() : undefined;
  }, [flagValue]);
}

export function useInboxFeatureBannerHasContent(config: InboxFeatureBannerConfig) {
  const content = useInboxFeatureBannerContent(config);

  return content !== null;
}
